import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCookieConsent } from '../context/CookieContext';
import { cookieCategoryInfo, CookieCategories } from '../utils/cookieConsent';

const CookieSettings = () => {
  const {
    showSettings,
    closeSettings,
    cookiePreferences,
    updatePreferences,
    acceptAll,
    acceptNecessary
  } = useCookieConsent();

  const [localPreferences, setLocalPreferences] = useState({
    [CookieCategories.NECESSARY]: true,
    [CookieCategories.FUNCTIONAL]: false,
    [CookieCategories.ANALYTICS]: false,
    [CookieCategories.MARKETING]: false
  });

  useEffect(() => {
    if (cookiePreferences) {
      setLocalPreferences({
        [CookieCategories.NECESSARY]: cookiePreferences[CookieCategories.NECESSARY] || true,
        [CookieCategories.FUNCTIONAL]: cookiePreferences[CookieCategories.FUNCTIONAL] || false,
        [CookieCategories.ANALYTICS]: cookiePreferences[CookieCategories.ANALYTICS] || false,
        [CookieCategories.MARKETING]: cookiePreferences[CookieCategories.MARKETING] || false
      });
    }
  }, [cookiePreferences, showSettings]);

  const handleToggle = (category) => {
    if (category === CookieCategories.NECESSARY) return; // Cannot disable necessary cookies

    setLocalPreferences((prev) => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSave = () => {
    updatePreferences(localPreferences);
  };

  return (
    <Dialog
      open={showSettings}
      onClose={closeSettings}
      maxWidth="md"
      fullWidth
      scroll="paper"
      sx={{
        zIndex: 10000
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, flexShrink: 0 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          Cookie Settings
        </Typography>
        <IconButton onClick={closeSettings} size="small" edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 2, overflowY: 'auto', flex: 1 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" color="text.secondary" paragraph>
            We use cookies to enhance your experience on our website. You can choose which types of cookies
            to allow. Please note that blocking some types of cookies may impact your experience.
          </Typography>
        </Box>

        {Object.values(CookieCategories).map((category, index) => {
          const info = cookieCategoryInfo[category];
          const isEnabled = localPreferences[category];
          const isRequired = info.required;

          return (
            <Box key={category}>
              {index > 0 && <Divider sx={{ my: 2 }} />}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 0.5 }}>
                    {info.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 0 }}>
                    {info.description}
                  </Typography>
                  {isRequired && (
                    <Typography variant="caption" color="primary" sx={{ fontStyle: 'italic', mt: 0.5, display: 'block' }}>
                      Always Active
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', pt: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isEnabled}
                        onChange={() => handleToggle(category)}
                        disabled={isRequired}
                        color="primary"
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
        <Button
          onClick={acceptNecessary}
          variant="outlined"
          sx={{
            textTransform: 'none',
            borderColor: 'grey.400',
            color: 'text.primary',
            '&:hover': {
              borderColor: 'grey.600',
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          Necessary Only
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleSave}
          variant="outlined"
          sx={{
            textTransform: 'none',
            borderColor: 'primary.main',
            color: 'primary.main',
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'rgba(156, 39, 176, 0.04)'
            }
          }}
        >
          Save Preferences
        </Button>
        <Button
          onClick={acceptAll}
          variant="contained"
          sx={{
            textTransform: 'none',
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          }}
        >
          Accept All
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CookieSettings;
